import React from 'react';
import type { EmployeeListResponse } from 'src/api/generated';
import type { ComponentSize } from '../../../types/component.type';
import type { TagDropdownOption } from './tag-dropdown';
import { TagDropdown } from './tag-dropdown';
import { Text } from '../../atoms/text';
import { useAllUsers } from 'src/modules/shared/hooks/api/admin/users';
import { useEmployees } from 'src/modules/shared/hooks/api/automations/employees/employees';
import { PiUserLight } from 'react-icons/pi';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import {
  createAssigneeValue,
  parseAssigneeValue,
  getUserDisplayName,
  getEmployeeDisplayName,
} from '../../../utils/tasks/assignee.util';
import { getControlTextSize } from '../../../components/atoms/text/text.styles';

export interface AssigneeValue {
  type: 'user' | 'ai_employee';
  id: string;
}

interface AssigneeDropdownProps {
  value?: AssigneeValue;
  onAssigneeChange: (assignee: AssigneeValue) => Promise<void>;
  disabled?: boolean;
  size?: ComponentSize;
  className?: string;
  placeholder?: string;
}

/**
 * Generic assignee dropdown component that can be used for any assignment
 */
export const AssigneeDropdown: React.FC<AssigneeDropdownProps> = ({
  value,
  onAssigneeChange,
  disabled = false,
  size = 'xs',
  className = '',
  placeholder = 'Sin asignar',
}) => {
  const { users } = useAllUsers();
  const { employees } = useEmployees();

  const separator = ':';
  const textSize = getControlTextSize(size);

  const createAssigneeOptions = (): TagDropdownOption[] => {
    const currentAssigneeId = value
      ? createAssigneeValue(value.type, value.id, separator)
      : undefined;

    const userOptions: TagDropdownOption[] = users.map((user) => {
      const userId = createAssigneeValue('user', user.id.toString(), separator);

      return {
        id: userId,
        content: getUserDisplayName(user),
        icon: <PiUserLight className="h-4 w-4 text-secondary-600" />,
        selected: userId === currentAssigneeId,
      };
    });

    const employeeOptions: TagDropdownOption[] = employees.map(
      (employee: EmployeeListResponse['items'][number]) => {
        const employeeId = createAssigneeValue(
          'ai_employee',
          employee.employeeId,
          separator
        );

        return {
          id: employeeId,
          content: getEmployeeDisplayName(employee),
          icon: <AiRobotOutlineIcon className="h-4 w-4 text-secondary-600" />,
          selected: employeeId === currentAssigneeId,
        };
      }
    );

    // Build options with headers and separators
    const options: TagDropdownOption[] = [];

    if (userOptions.length > 0) {
      options.push(
        {
          id: 'users-header',
          content: 'Usuarios',
          disabled: true,
          selected: false,
        },
        ...userOptions
      );
    }

    if (employeeOptions.length > 0) {
      if (userOptions.length > 0) {
        const lastUserIndex = options.length - 1;
        options[lastUserIndex] = {
          ...options[lastUserIndex],
          separator: true,
        };
      }

      options.push(
        {
          id: 'employees-header',
          content: 'Agentes IA',
          disabled: true,
          selected: false,
        },
        ...employeeOptions
      );
    }

    return options;
  };

  const handleAssigneeUpdate = async (option: TagDropdownOption) => {
    const { type, id } = parseAssigneeValue(String(option.id), separator);

    if (type === 'users-header' || type === 'employees-header') return;

    await onAssigneeChange({
      type: type as 'user' | 'ai_employee',
      id,
    });
  };

  const getDisplayName = (): string => {
    if (!value) return placeholder;

    if (value.type === 'user') {
      const user = users.find((u) => u.id.toString() === value.id);
      return user ? getUserDisplayName(user) : 'Usuario desconocido';
    } else {
      const employee = employees.find(
        (e: EmployeeListResponse['items'][number]) => e.employeeId === value.id
      );
      return employee ? getEmployeeDisplayName(employee) : 'IA sin nombre';
    }
  };

  return (
    <div className={`flex flex-shrink-0 w-[120px] items-center ${className}`}>
      <TagDropdown
        content={
          <Text size={textSize} className="truncate block w-full max-w-[82px]">
            {getDisplayName()}
          </Text>
        }
        variant="primary"
        appearance="outline"
        size={size}
        options={createAssigneeOptions()}
        onOptionSelect={(option) => {
          void handleAssigneeUpdate(option);
        }}
        disabled={disabled}
      />
    </div>
  );
};
